import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { useJoinLeague } from "@/modules/leagues/hooks/useJoinLeague";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";

/**
 * "Entrar em uma liga" — fluxo separado da criação (Priority 3 do escopo
 * "bolão funcional de verdade"). Antes disso, o botão "Tenho um código"
 * caía incorretamente no wizard de criação de liga.
 */
export function JoinLeaguePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const joinLeague = useJoinLeague();
  const [code, setCode] = useState("");

  function handleSubmit() {
    if (!code.trim() || joinLeague.isPending) return; // trava clique/Enter duplicado — evita várias tentativas simultâneas da mesma entrada
    joinLeague.mutate(code, {
      onSuccess: (league) => {
        toast({ title: "Você entrou na liga!", description: league.nome });
        navigate(ROUTES.leaguePredictions(league.id));
      },
      onError: (err) => {
        toast({
          title: "Não foi possível entrar na liga",
          description: err instanceof Error ? err.message : "Tente novamente.",
          variant: "destructive",
        });
      },
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(ROUTES.groups)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Entrar em uma liga</h1>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200">
          <KeyRound className="h-5 w-5" />
        </div>
        <p className="text-sm text-muted-foreground">
          Peça o código de convite pra quem administra a liga e digite abaixo.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="invite-code">Código da liga</Label>
        <Input
          id="invite-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Ex: AMIGOS27"
          className="text-center font-display text-lg tracking-widest"
          maxLength={12}
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && !joinLeague.isPending && handleSubmit()}
        />
      </div>

      <Button className="w-full" disabled={!code.trim() || joinLeague.isPending} isLoading={joinLeague.isPending} onClick={handleSubmit}>
        Entrar na liga
      </Button>
    </div>
  );
}
